/**
 * Amanah — Knowledge Posters config
 *
 * Static registry of shareable, teaching-first posters shown in the
 * `KnowledgePosters` grid on the home page. To add a new poster:
 *
 *   1. Drop the high-res PNG into `frontend/public/posters/`
 *      (recommended: 1024×1536 or larger, PNG, filename kebab-case)
 *   2. Append an entry below with title / series / seriesNum / source /
 *      image / and a short seoDescription used by the download filename.
 *
 * The `status: 'live'|'coming-soon'` flag controls whether the tile is
 * interactive. Coming-soon tiles render as skeleton teasers.
 *
 * All posters are served from `/posters/…` and can be downloaded as PNG
 * or PDF from the modal. No backend involvement, no state stored — pure
 * static asset delivery in keeping with the app's privacy-first design.
 */
export const POSTERS = [
  {
    id: 'ibn-ashur-circulation-of-wealth-01',
    title: 'Circulation of Wealth',
    subtitle: "Ibn Ashur · Maqasid al Shari'ah",
    series: 'Ibn Ashur',
    seriesNum: 1,
    theme: 'wealth',
    kicker: 'Ethical Wealth',
    excerpt:
      'The transfer of wealth in the community among as many hands as possible without causing harm to those who acquired it lawfully.',
    tags: ['Zakah', 'Trade', 'Maqasid'],
    source:
      "Excerpted from Ibn Ashur's Treatise on Maqasid al Shari'ah (IIIT, translated & annotated by Mohamed El-Tahir El-Mesawi). pp. 288-289.",
    image: '/posters/ibn-ashur-circulation-of-wealth-01.png',
    thumb: '/posters/ibn-ashur-circulation-of-wealth-01.png',
    status: 'live',
    publishedAt: '2026-02',
  },
  {
    id: 'ibn-ashur-circulation-of-wealth-02',
    title: 'Circulation of Wealth · Part 2',
    subtitle: "Ibn Ashur · Maqasid al Shari'ah",
    series: 'Ibn Ashur',
    seriesNum: 2,
    theme: 'wealth',
    kicker: 'Ethical Wealth',
    excerpt:
      'The purpose of inheritance is justice, mercy and the circulation of wealth.',
    tags: ['Inheritance', 'Justice', 'Women'],
    source:
      "Excerpted from Ibn Ashur's Treatise on Maqāṣid al-Shariʿah (a IIIT Publication. Translated and annotated by Mohamed El-Tahir El-Mesawi). pp. 289-290.",
    image: '/posters/ibn-ashur-circulation-of-wealth-02.png',
    thumb: '/posters/ibn-ashur-circulation-of-wealth-02.png',
    status: 'live',
    publishedAt: '2026-02',
  },
  {
    id: 'ibn-ashur-circulation-of-wealth-03',
    title: 'Luxury, Trade & the Flow of Wealth',
    subtitle: "Ibn Ashur · Maqasid al Shari'ah",
    series: 'Ibn Ashur',
    seriesNum: 3,
    theme: 'wealth',
    kicker: 'Ethical Wealth',
    excerpt:
      'Wealth is a trust from Allah — use it with wisdom, share it with compassion, and circulate it with purpose.',
    tags: ['Trade', 'Luxury', 'Circulation'],
    source:
      "Excerpted from Ibn Ashur's Treatise on Maqāṣid al-Shariʿah (a IIIT Publication. Translated and annotated by Mohamed El-Tahir El-Mesawi). pp. 291-294.",
    image: '/posters/ibn-ashur-circulation-of-wealth-03.png',
    thumb: '/posters/ibn-ashur-circulation-of-wealth-03.png',
    status: 'live',
    publishedAt: '2026-02',
  },
  {
    id: 'ibn-ashur-circulation-of-wealth-04',
    title: 'Money as a Trusted Medium',
    subtitle: "Ibn Ashur · Maqasid al Shari'ah",
    series: 'Ibn Ashur',
    seriesNum: 4,
    theme: 'wealth',
    kicker: 'Ethical Wealth',
    excerpt:
      'Money is a trusted medium (naqdayn) that keeps wealth moving and society thriving — increasing halal transactions is itself a Sharī‘ah objective.',
    tags: ['Money', 'Trade', 'Naqdayn'],
    source:
      "Excerpted from Ibn Ashur's Treatise on Maqāṣid al-Shariʿah (a IIIT Publication. Translated and annotated by Mohamed El-Tahir El-Mesawi). p. 295.",
    image: '/posters/ibn-ashur-circulation-of-wealth-04.png',
    thumb: '/posters/ibn-ashur-circulation-of-wealth-04.png',
    status: 'live',
    publishedAt: '2026-03',
  },
  {
    id: 'ibn-ashur-transparency-in-wealth-01',
    title: 'Transparency in Wealth & Property',
    subtitle: "Ibn Ashur · Maqasid al Shari'ah · Part 1",
    series: 'Ibn Ashur',
    seriesNum: 5,
    theme: 'wealth',
    kicker: 'Ethical Wealth',
    excerpt:
      'Wuḍūḥ al-amwāl — transparency in wealth and property prevents harm, builds trust, and protects rights, individual and communal.',
    tags: ['Transparency', 'Trust', 'Property'],
    source:
      "Excerpted from Ibn Ashur's Treatise on Maqāṣid al-Shariʿah (a IIIT Publication. Translated and annotated by Mohamed El-Tahir El-Mesawi). pp. 296-297.",
    image: '/posters/ibn-ashur-transparency-in-wealth-01.png',
    thumb: '/posters/ibn-ashur-transparency-in-wealth-01.png',
    status: 'live',
    publishedAt: '2026-03',
  },
  {
    id: 'ibn-ashur-transparency-in-wealth-02',
    title: 'Transparency in Wealth & Property · Part 2',
    subtitle: "Ibn Ashur · Maqasid al Shari'ah · Part 2",
    series: 'Ibn Ashur',
    seriesNum: 6,
    theme: 'wealth',
    kicker: 'Ethics · Trust · A Stronger Society',
    excerpt:
      'Rulers and guardians must protect public wealth — regulating trade, markets, zakat accounts (maṣārif) and public endowments (awqāf ʿāmmah), and safeguarding what is entrusted (Qur\u2019an 4:5, 4:6).',
    tags: ['Transparency', 'Public Wealth', 'Awqaf', 'Trust'],
    source:
      "Excerpted from Ibn Ashur's Treatise on Maqāṣid al-Shariʿah (a IIIT Publication. Translated and annotated by Mohamed El-Tahir El-Mesawi). pp. 296-297.",
    image: '/posters/ibn-ashur-transparency-in-wealth-02.png',
    thumb: '/posters/ibn-ashur-transparency-in-wealth-02.png',
    status: 'live',
    publishedAt: '2026-02',
  },
  {
    id: 'ibn-ashur-transparency-in-wealth-03',
    title: 'Transparency in Wealth & Property · Part 3',
    subtitle: "Ibn Ashur · Maqasid al Shari'ah · Part 3",
    series: 'Ibn Ashur',
    seriesNum: 7,
    theme: 'wealth',
    kicker: 'Ethics · Trust · A Stronger Society',
    excerpt:
      'Documenting property, honouring exclusive lawful ownership (Qur\u2019an 2:282) and disposing of wealth without harming others — the Shari\u2019ah\u2019s twin safeguards for property rights.',
    tags: ['Contracts', 'Property Rights', 'No Harm'],
    source:
      "Excerpted from Ibn Ashur's Treatise on Maqāṣid al-Shariʿah (a IIIT Publication. Translated and annotated by Mohamed El-Tahir El-Mesawi). pp. 297-298.",
    image: '/posters/ibn-ashur-transparency-in-wealth-03.png',
    thumb: '/posters/ibn-ashur-transparency-in-wealth-03.png',
    status: 'live',
    publishedAt: '2026-02',
  },
];

export const getLivePosters = () => POSTERS.filter((p) => p.status === 'live');
